import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';

// ── Types ──

export type MonitorType =
  | 'domain_watchdog'
  | 'person_watchdog'
  | 'ip_watchdog'
  | 'keyword_watchdog'
  | 'dark_web_watchdog';

export type MonitorStatus = 'active' | 'paused' | 'alert' | 'error';

export interface Monitor {
  id: string;
  name: string;
  target: string;
  monitor_type: MonitorType;
  interval_minutes: number;
  is_active: boolean;
  alerts_enabled: boolean;
  status: MonitorStatus;
  last_check: string | null;
  next_check: string | null;
  checks_count: number;
  alerts_count: number;
  last_result: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface MonitorCreateRequest {
  name: string;
  target: string;
  monitor_type: MonitorType;
  interval_minutes: number;
  alerts_enabled: boolean;
}

export interface MonitorUpdateRequest {
  name?: string;
  interval_minutes?: number;
  alerts_enabled?: boolean;
  is_active?: boolean;
}

export interface MonitorStats {
  total_monitors: number;
  active_monitors: number;
  paused_monitors: number;
  error_monitors: number;
  total_checks: number;
  total_alerts: number;
  monitors_by_type: Record<string, number>;
  avg_check_interval_minutes: number;
  uptime_percentage: number;
}

export interface MonitorTypeInfo {
  id: MonitorType;
  name: string;
  description: string;
  icon: string;
  default_interval: number;
}

export interface CheckResult {
  type: string;
  target: string;
  status: string;
  checked_at: string;
  check_id: string;
  alerts?: string[];
  [key: string]: unknown;
}

export interface MonitorDetail {
  monitor: Monitor;
  check_history?: CheckResult[];
  total_checks_stored?: number;
}

export interface MonitorListResponse {
  monitors: Monitor[];
  total: number;
}

// ── API Functions ──

async function fetchMonitors(params?: {
  monitor_type?: string;
  status?: string;
  is_active?: boolean;
}): Promise<MonitorListResponse> {
  const { data } = await apiClient.get('/monitoring/monitors', { params });
  return data;
}

async function fetchMonitorStats(): Promise<MonitorStats> {
  const { data } = await apiClient.get('/monitoring/monitors/stats');
  return data;
}

async function fetchMonitorDetail(monitorId: string): Promise<MonitorDetail> {
  const { data } = await apiClient.get(`/monitoring/monitors/${monitorId}`);
  return data;
}

async function fetchMonitorTypes(): Promise<MonitorTypeInfo[]> {
  const { data } = await apiClient.get('/monitoring/types');
  return data.types;
}

async function createMonitor(request: MonitorCreateRequest): Promise<{ status: string; monitor: Monitor; initial_check: CheckResult }> {
  const { data } = await apiClient.post('/monitoring/monitors', request);
  return data;
}

async function deleteMonitor(monitorId: string): Promise<{ status: string; monitor_id: string; name: string }> {
  const { data } = await apiClient.delete(`/monitoring/monitors/${monitorId}`);
  return data;
}

async function forceCheck(monitorId: string): Promise<{ status: string; monitor: Monitor; result: CheckResult; processing_time_ms: number }> {
  const { data } = await apiClient.post(`/monitoring/monitors/${monitorId}/check`);
  return data;
}

async function updateMonitor(
  monitorId: string,
  request: MonitorUpdateRequest,
): Promise<{ status: string; monitor: Monitor }> {
  const { data } = await apiClient.patch(`/monitoring/monitors/${monitorId}`, request);
  return data;
}

// ── React Query Hooks ──

export function useMonitors(params?: { monitor_type?: string; status?: string; is_active?: boolean }) {
  return useQuery({
    queryKey: ['monitors', params],
    queryFn: () => fetchMonitors(params),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useMonitorStats() {
  return useQuery({
    queryKey: ['monitor-stats'],
    queryFn: fetchMonitorStats,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useMonitorDetail(monitorId: string | null) {
  return useQuery({
    queryKey: ['monitor-detail', monitorId],
    queryFn: () => fetchMonitorDetail(monitorId!),
    enabled: !!monitorId,
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

export function useMonitorTypes() {
  return useQuery({
    queryKey: ['monitor-types'],
    queryFn: fetchMonitorTypes,
    staleTime: 60_000 * 60, // Types rarely change
  });
}

export function useCreateMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMonitor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] });
      queryClient.invalidateQueries({ queryKey: ['monitor-stats'] });
    },
  });
}

export function useDeleteMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMonitor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] });
      queryClient.invalidateQueries({ queryKey: ['monitor-stats'] });
    },
  });
}

export function useForceCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: forceCheck,
    onSuccess: (_data, monitorId) => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] });
      queryClient.invalidateQueries({ queryKey: ['monitor-detail', monitorId] });
      queryClient.invalidateQueries({ queryKey: ['monitor-stats'] });
    },
  });
}

export function useUpdateMonitor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ monitorId, ...request }: MonitorUpdateRequest & { monitorId: string }) =>
      updateMonitor(monitorId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] });
      queryClient.invalidateQueries({ queryKey: ['monitor-detail', variables.monitorId] });
      queryClient.invalidateQueries({ queryKey: ['monitor-stats'] });
    },
  });
}
